import { api } from '../client';
import { READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS } from '../web-runtime-policy';

import type {
  ListResponse,
  MovementSourceType,
  MovementType,
  Paging,
  PaginationParams,
  StockMovement,
} from './common';

export type AlertStatus = 'negative' | 'zero' | 'low' | 'ok';

/** Current balance for a product at a location */
export interface Balance {
  product_id: string;
  location_id: string;
  balance: string;
  last_movement_date: string | null;
  last_movement_at?: string | null;
  min_quantity?: string | null;
  alert_status?: AlertStatus;
  product_name?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  unit_code?: string | null;
  is_weighted?: boolean | null;
  location_name?: string | null;
  /** True when the location has not completed its first FULL inventory yet. */
  requires_inventory: boolean;
  /**
   * Seller-workspace only: quantity already hanging on this source location in
   * request-flow transfer requests that have not been shipped yet (в заявках).
   */
  pending_request_quantity?: string | null;
  /**
   * Seller-workspace only: quantity shipped from this location in transfer
   * requests awaiting admin approval (отгружено, ledger not yet posted).
   */
  shipped_out_quantity?: string | null;
  /**
   * Seller-workspace only: quantity heading to this location in transfer
   * requests awaiting admin approval (в пути).
   */
  incoming_quantity?: string | null;
  /**
   * Seller-workspace only: on_hand − shipped_out − reserved (доступно). Can be
   * negative when the location has over-promised.
   */
  available_quantity?: string | null;
}

/** Balance with threshold alert info */
export interface BalanceWithAlert extends Balance {
  min_quantity: string | null;
  alert_status: AlertStatus;
}

/** Balance history entry */
export interface BalanceHistoryEntry {
  id: string;
  movement_type: MovementType | Lowercase<MovementType>;
  quantity: string;
  stock_uom_code: string;
  counted_quantity?: string | null;
  unit_cost?: string | null;
  unit_price?: string | null;
  source_type: MovementSourceType;
  source_id: string;
  line_ref?: string | null;
  /** ISO datetime (preferred). */
  effective_at?: string | null;
  effective_date: string;
  is_cancelled: boolean;
  notes?: string | null;
  created_at?: string | null;
  running_balance?: string | null;
}

/** Balance filters */
export interface BalanceFilters extends PaginationParams {
  [key: string]: string | number | boolean | string[] | undefined;
  location_id?: string;
  location_ids?: string[];
  product_ids?: string[];
  search?: string;
  category_id?: string;
  /** Only products without a category (ignored when category_id is set). */
  uncategorized?: boolean;
  include_pending_requests?: boolean;
  exclude_transfer_request_id?: string;
  /** Optional ISO datetime to calculate balances as-of (e.g. document date/time). */
  at?: string;
  alert_status?: string;
  only_non_zero?: boolean;
  sort_by?: string;
}

/** Balances response */
export type BalancesResponse = ListResponse<BalanceWithAlert>;

// Keep UUID query strings comfortably below the reverse proxy request-line limit.
// A 169-product catalog request is already large enough to receive HTTP 414 in
// production, even though the endpoint's logical page size allows 200 items.
export const STOCK_BALANCE_PRODUCT_IDS_LIMIT = 100;
// One tab's read-web permits bound its chunk fan-out; issuing more only queues
// in the browser gate.
export const BALANCE_BATCH_CONCURRENCY = READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS;

/**
 * Fetch a complete balance set when a caller supplies explicit product IDs.
 * Non-ID reads keep their original paging semantics; explicit-ID reads are
 * normalized, split, fully paged, and merged atomically.
 */
export async function fetchCompleteBalancesByProductIds(
  filters: BalanceFilters,
  productIdsLimit: number,
  fetchPage: (chunkFilters: BalanceFilters) => Promise<BalancesResponse>,
): Promise<BalancesResponse> {
  if (!Number.isInteger(productIdsLimit) || productIdsLimit < 1) {
    throw new Error('Balance product ID limit must be a positive integer');
  }
  const requestedProductIds = filters.product_ids;
  if (!requestedProductIds || requestedProductIds.length === 0) return fetchPage(filters);

  const productIds = Array.from(new Set(requestedProductIds.filter(Boolean))).sort();
  if (productIds.length === 0) throw new Error('Balance product IDs must not be empty');

  const {
    limit: _limit,
    offset: _offset,
    page: _page,
    page_size: _pageSize,
    product_ids: _productIds,
    ...baseFilters
  } = filters;
  const chunks: string[][] = [];
  for (let index = 0; index < productIds.length; index += productIdsLimit) {
    chunks.push(productIds.slice(index, index + productIdsLimit));
  }

  const chunkResponses: BalancesResponse[] = [];
  for (let index = 0; index < chunks.length; index += BALANCE_BATCH_CONCURRENCY) {
    const batch = chunks.slice(index, index + BALANCE_BATCH_CONCURRENCY);
    chunkResponses.push(
      ...(await Promise.all(
        batch.map(async (productIdsChunk) => {
          const pages: BalancesResponse[] = [];
          let offset = 0;

          for (;;) {
            const pageResponse = await fetchPage({
              ...baseFilters,
              product_ids: productIdsChunk,
              limit: productIdsLimit,
              offset,
            });
            pages.push(pageResponse);
            if (!pageResponse.paging.has_more) break;

            const nextOffset = pageResponse.paging.offset + pageResponse.items.length;
            if (nextOffset <= offset) {
              throw new Error('Balance pagination did not advance');
            }
            offset = nextOffset;
          }

          const firstPage = pages[0];
          const lastPage = pages[pages.length - 1];
          if (!firstPage || !lastPage) throw new Error('Balance batching produced no requests');
          const items = pages.flatMap((pageResponse) => pageResponse.items);
          return {
            ...firstPage,
            request_id: lastPage.request_id || firstPage.request_id,
            items,
            paging: {
              limit: items.length,
              offset: 0,
              total: Math.max(lastPage.paging.total, items.length),
              has_more: false,
            },
          };
        }),
      )),
    );
  }

  const firstPage = chunkResponses[0];
  const lastPage = chunkResponses[chunkResponses.length - 1];
  if (!firstPage || !lastPage) throw new Error('Balance batching produced no requests');
  const items = chunkResponses.flatMap((chunkResponse) => chunkResponse.items);

  return {
    ...firstPage,
    request_id: lastPage.request_id || firstPage.request_id,
    items,
    paging: {
      limit: items.length,
      offset: 0,
      total: chunkResponses.reduce((total, chunkResponse) => total + chunkResponse.paging.total, 0),
      has_more: false,
    },
  };
}

/** Product balance detail response (balances + movement history) */
export interface ProductBalanceDetailResponse {
  request_id: string;
  balances: Balance[];
  history: StockMovement[];
  history_paging?: Paging;
}

/** Server cap for one location history request (`limit: Query(le=5000)`). */
export const LOCATION_HISTORY_MAX_LIMIT = 5000;

/** Location history response (movement history with running balance) */
export interface LocationHistoryResponse {
  request_id: string;
  /** Newest movements first-limited; chronological order is restored client-side. */
  items: BalanceHistoryEntry[];
  /** True when the range holds more movements than the limit (older ones omitted). */
  has_more: boolean;
}

export interface AggregateBalanceHistoryItem {
  effective_date: string;
  closing_balance: string;
  delta?: string | null;
}

export interface AggregateBalanceHistoryResponse {
  request_id: string;
  items: AggregateBalanceHistoryItem[];
}

/** Alerts summary response */
export interface AlertsResponse {
  request_id: string;
  summary: {
    negative: number;
    zero: number;
    low: number;
    ok: number;
  };
}

export interface BalanceMatrixLocation {
  location_id: string;
  location_name: string;
  requires_inventory: boolean;
}

export interface BalanceMatrixCell {
  location_id: string;
  balance: string;
  alert_status: AlertStatus;
  requires_inventory: boolean;
}

export interface BalanceMatrixItem {
  product_id: string;
  product_name?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  unit_code?: string | null;
  is_weighted?: boolean | null;
  ownership?: string | null;
  balances: BalanceMatrixCell[];
}

export interface BalanceMatrixViewResponse {
  request_id: string;
  locations: BalanceMatrixLocation[];
  items: BalanceMatrixItem[];
}

export const stockBalancesApi = {
  list: (filters: BalanceFilters = {}): Promise<BalancesResponse> =>
    fetchCompleteBalancesByProductIds(filters, STOCK_BALANCE_PRODUCT_IDS_LIMIT, (chunkFilters) =>
      api.webAdmin.get('/stock/balances', { params: chunkFilters }),
    ),

  detail: (
    productId: string,
    filters: { page?: number; page_size?: number } = {},
  ): Promise<ProductBalanceDetailResponse> =>
    api.webAdmin.get(`/stock/balances/${productId}`, { params: filters }),

  matrix: (
    filters: { location_id?: string; category_id?: string } = {},
  ): Promise<{ request_id: string; matrix: Record<string, Record<string, string>> }> =>
    api.webAdmin.get('/stock/balances/matrix', { params: filters }),

  matrixView: (
    filters: {
      location_ids?: string[];
      category_id?: string;
      category_ids?: string[];
      search?: string;
    } = {},
  ): Promise<BalanceMatrixViewResponse> =>
    api.webAdmin.get('/stock/balances/matrix-view', { params: filters }),

  balancesMatrixXlsxUrl: (
    filters: { location_ids?: string[]; category_ids?: string[]; search?: string } = {},
  ): string => {
    const params = new URLSearchParams();

    for (const locationId of filters.location_ids ?? []) {
      params.append('location_ids', locationId);
    }
    for (const categoryId of filters.category_ids ?? []) {
      params.append('category_ids', categoryId);
    }
    if (filters.search) {
      params.set('search', filters.search);
    }

    const query = params.toString();
    return `/api/web-admin/stock/balances/export/xlsx${query ? `?${query}` : ''}`;
  },

  movementReportXlsxUrl: (filters: {
    product_ids: string[];
    location_id: string;
    date_from: string;
    date_to: string;
  }): string => {
    const params = new URLSearchParams();
    for (const productId of filters.product_ids) {
      params.append('product_ids', productId);
    }
    params.set('location_id', filters.location_id);
    params.set('date_from', filters.date_from);
    params.set('date_to', filters.date_to);
    return `/api/web-admin/stock/balances/movement-report/xlsx?${params.toString()}`;
  },

  snapshotReportCsvUrl: (filters: {
    product_ids: string[];
    location_id: string;
    report_at: string;
  }): string => {
    const params = new URLSearchParams();
    for (const productId of filters.product_ids) {
      params.append('product_ids', productId);
    }
    params.set('location_id', filters.location_id);
    params.set('report_at', filters.report_at);
    return `/api/web-admin/stock/balances/snapshot-report/csv?${params.toString()}`;
  },

  alerts: (filters: { location_id?: string; search?: string } = {}): Promise<AlertsResponse> =>
    api.webAdmin.get('/stock/alerts', { params: filters }),

  atDate: (
    productId: string,
    locationId: string | null,
    date: string,
  ): Promise<{ request_id: string; balance: string; date: string }> =>
    api.webAdmin.get('/stock/balances/history', {
      params: { product_id: productId, location_id: locationId, date },
    }),

  locationHistory: (
    productId: string,
    locationId: string,
    filters: { date_from?: string; date_to?: string; limit?: number } = {},
  ): Promise<LocationHistoryResponse> =>
    api.webAdmin.get(`/stock/balances/${productId}/${locationId}/history`, {
      params: filters,
    }),

  aggregateHistory: (
    productId: string,
    filters: { date_from?: string; date_to?: string } = {},
  ): Promise<AggregateBalanceHistoryResponse> =>
    api.webAdmin.get(`/stock/balances/${productId}/history`, { params: filters }),
};
